import {
  coreExtensionData,
  createExtension,
  createFrontendModule,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  AppRootWrapperBlueprint,
  SignInPageBlueprint,
} from '@backstage/plugin-app-react';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssessmentIcon from '@material-ui/icons/Assessment';
import DnsIcon from '@material-ui/icons/Dns';
import ExtensionIcon from '@material-ui/icons/Extension';
import TimelineIcon from '@material-ui/icons/Timeline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { FactoryBackstageCapabilitiesPage } from './FactoryBackstageCapabilitiesPage';
import { FactoryKubernetesPage } from './FactoryKubernetesPage';
import { FactoryProductVisualizerPage } from './FactoryProductVisualizerPage';
import { FactoryProjectInsightsPage } from './FactoryProjectInsightsPage';
import { FactoryProductsPage } from './FactoryProductsPage';
import { FactoryReleaseEvidencePage } from './FactoryReleaseEvidencePage';
import { FactoryTechRadarPage } from './FactoryTechRadarPage';
import { GuestAuthSessionWrapper } from './GuestAuthSessionWrapper';
import { FactoryWorkflowsPage } from './FactoryWorkflowsPage';
import { FactoryPortalSignInPage } from './FactoryPortalSignInPage';
import { keycloakAuthApiExtension } from './keycloakAuthApi';

const factoryWorkflowsRoute = createExtension({
  kind: 'page',
  name: 'factory-workflows',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/factory-workflows'),
      coreExtensionData.reactElement(<FactoryWorkflowsPage />),
      coreExtensionData.title('Factory Workflows'),
      coreExtensionData.icon(<AccountTreeIcon />),
    ];
  },
});

const factoryProductsRoute = createExtension({
  kind: 'page',
  name: 'factory-products',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/factory-products'),
      coreExtensionData.reactElement(<FactoryProductsPage />),
      coreExtensionData.title('Factory Products'),
      coreExtensionData.icon(<AssessmentIcon />),
    ];
  },
});

const factoryKubernetesRoute = createExtension({
  kind: 'page',
  name: 'kubernetes',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/kubernetes'),
      coreExtensionData.reactElement(<FactoryKubernetesPage />),
      coreExtensionData.title('Kubernetes'),
      coreExtensionData.icon(<DnsIcon />),
    ];
  },
});

const factoryBackstageCapabilitiesRoute = createExtension({
  kind: 'page',
  name: 'backstage-capabilities',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/backstage-capabilities'),
      coreExtensionData.reactElement(<FactoryBackstageCapabilitiesPage />),
      coreExtensionData.title('Backstage Capabilities'),
      coreExtensionData.icon(<ExtensionIcon />),
    ];
  },
});

const factoryProjectInsightsRoute = createExtension({
  kind: 'page',
  name: 'project-insights',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/project-insights'),
      coreExtensionData.reactElement(<FactoryProjectInsightsPage />),
      coreExtensionData.title('Project Work Insights'),
      coreExtensionData.icon(<AssessmentIcon />),
    ];
  },
});

const factoryProjectInsightsLegacyRoute = createExtension({
  kind: 'page',
  name: 'project-insights-legacy-cost-path',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/cost-insights'),
      coreExtensionData.reactElement(<FactoryProjectInsightsPage />),
      coreExtensionData.title('Project Work Insights'),
      coreExtensionData.icon(<AssessmentIcon />),
    ];
  },
});

const factoryProductVisualizerRoute = createExtension({
  kind: 'page',
  name: 'product-visualizer',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/product-visualizer'),
      coreExtensionData.reactElement(<FactoryProductVisualizerPage />),
      coreExtensionData.title('Product Visualizer'),
      coreExtensionData.icon(<AccountTreeIcon />),
    ];
  },
});

const factoryTechRadarRoute = createExtension({
  kind: 'page',
  name: 'tech-radar',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/tech-radar'),
      coreExtensionData.reactElement(<FactoryTechRadarPage />),
      coreExtensionData.title('Tech Radar'),
      coreExtensionData.icon(<TimelineIcon />),
    ];
  },
});

const factoryReleaseEvidenceRoute = createExtension({
  kind: 'page',
  name: 'release-evidence',
  attachTo: { id: 'app/routes', input: 'routes' },
  output: [
    coreExtensionData.routePath,
    coreExtensionData.reactElement,
    coreExtensionData.title,
    coreExtensionData.icon,
  ],
  factory() {
    return [
      coreExtensionData.routePath('/release-evidence'),
      coreExtensionData.reactElement(<FactoryReleaseEvidencePage />),
      coreExtensionData.title('Release Evidence'),
      coreExtensionData.icon(<VerifiedUserIcon />),
    ];
  },
});

export const factoryPortalPlugin = createFrontendPlugin({
  pluginId: 'c4ci-factory-portal',
  extensions: [
    factoryProductsRoute,
    factoryWorkflowsRoute,
    factoryKubernetesRoute,
    factoryBackstageCapabilitiesRoute,
    factoryProjectInsightsRoute,
    factoryProjectInsightsLegacyRoute,
    factoryProductVisualizerRoute,
    factoryTechRadarRoute,
    factoryReleaseEvidenceRoute,
  ],
});

export const factoryPortalAuthModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    keycloakAuthApiExtension,
    AppRootWrapperBlueprint.make({
      params: {
        component: ({ children }) => (
          <GuestAuthSessionWrapper>{children}</GuestAuthSessionWrapper>
        ),
      },
    }),
    SignInPageBlueprint.make({
      params: {
        loader: async () => FactoryPortalSignInPage,
      },
    }),
  ],
});
